import { addDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/**
 * All dates are shown in the STUDIO's timezone (from the database), never
 * the device's, so a client travelling or an owner in another city sees the
 * same wall-clock times as the studio.
 */
export function fmtInTz(date: Date | string, tz: string, pattern: string): string {
  return formatInTimeZone(typeof date === 'string' ? new Date(date) : date, tz, pattern, { locale: ru });
}

export const fmtTime = (d: Date | string, tz: string) => fmtInTz(d, tz, 'HH:mm');
export const fmtDayLong = (d: Date | string, tz: string) => fmtInTz(d, tz, 'd MMMM, EEEE');
export const fmtDayShort = (d: Date | string, tz: string) => fmtInTz(d, tz, 'd MMM, EEEEEE');
export const fmtDateTime = (d: Date | string, tz: string) => fmtInTz(d, tz, 'd MMMM, HH:mm');

/** 'YYYY-MM-DD' local calendar date → Date at local midnight in tz. */
export function localDateToInstant(isoDate: string, tz: string): Date {
  return fromZonedTime(`${isoDate}T00:00:00`, tz);
}

export function addIsoDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const dt = addDays(new Date(Date.UTC(y, m - 1, d)), days);
  return dt.toISOString().slice(0, 10);
}

/** Monday of the ISO week containing isoDate. */
export function weekStart(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay() || 7;
  return addIsoDays(isoDate, 1 - dow);
}

export function dayLabel(isoDate: string, today: string, tz: string): string {
  if (isoDate === today) return 'Сегодня';
  if (isoDate === addIsoDays(today, 1)) return 'Завтра';
  return fmtInTz(localDateToInstant(isoDate, tz), tz, 'd MMMM, EEEEEE');
}

export function fmtMoney(cents: number, currency = 'RUB', from = false): string {
  const s = new Intl.NumberFormat('ru-RU', { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100);
  return from ? `от ${s}` : s;
}

function plural(n: number, one: string, few: string, many: string) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export function fmtDuration(min: number): string {
  if (min >= 1440 && min % 1440 === 0) {
    const d = min / 1440;
    return `${d} ${plural(d, 'день', 'дня', 'дней')}`;
  }
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} мин`;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

export const pluralRu = plural;

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
export const weekdayShort = (isoWeekday: number) => WEEKDAYS[isoWeekday - 1] ?? '';

export function fmtPhoneHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
